import React, { Component } from 'react';
import { saveImage } from '../../api/_products';

/**
 * RichTextEditor component wrapper for React Quill
 * Provides a WYSIWYG editor that outputs HTML
 * Only loads on client-side to avoid SSR issues
 *
 * @param {string} value - The HTML content
 * @param {function} onChange - Callback function when content changes
 * @param {string} placeholder - Placeholder text
 * @param {object} style - Custom styles for the editor container
 */
class RichTextEditor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      ReactQuill: null,
      Quill: null,
      mounted: false
    };
    this.handleChange = this.handleChange.bind(this);
    this.imageHandler = this.imageHandler.bind(this);
    this.quillRef = React.createRef();
  }

  componentDidMount() {
    // Only load on client side
    if (typeof window !== 'undefined') {
      // Load ReactQuill
      const ReactQuill = require('react-quill');
      require('react-quill/dist/quill.snow.css');

      // Load Quill and resize module
      const Quill = require('quill');

      // Only register imageResize module once globally
      if (!Quill.imports['modules/imageResize']) {
        const ImageResize = require('quill-image-resize-module-react').default;
        Quill.register('modules/imageResize', ImageResize);
      }

      // Register custom image format to preserve inline styles
      const ImageFormatAttributesList = [
        'alt', 'height', 'width', 'style'
      ];
      const BaseImageFormat = Quill.import('formats/image');
      class ImageFormat extends BaseImageFormat {
        static formats(domNode) {
          return ImageFormatAttributesList.reduce((formats, attribute) => {
            if (domNode.hasAttribute(attribute)) {
              formats[attribute] = domNode.getAttribute(attribute);
            }
            return formats;
          }, {});
        }
        format(name, value) {
          if (ImageFormatAttributesList.indexOf(name) > -1) {
            if (value) {
              this.domNode.setAttribute(name, value);
            } else {
              this.domNode.removeAttribute(name);
            }
          } else {
            super.format(name, value);
          }
        }
      }
      Quill.register(ImageFormat, true);

      // Suppress specific error from imageResize module when deleting selected images
      // This is a known bug in quill-image-resize-module-react
      if (!window._quillImageResizeErrorHandlerInstalled) {
        const originalError = window.console.error;
        window.console.error = function(...args) {
          const errorStr = args[0]?.toString() || '';
          const stackStr = args[0]?.stack || '';

          // Suppress the "Cannot read properties of undefined (reading 'find')" error
          // that occurs when deleting a selected image in the resize module
          if ((errorStr.includes('Cannot read properties of undefined') &&
               (errorStr.includes('find') || stackStr.includes('checkImage'))) ||
              stackStr.includes('image-resize.min.js')) {
            return; // Suppress this specific error
          }

          originalError.apply(console, args);
        };
        window._quillImageResizeErrorHandlerInstalled = true;
      }

      this.setState({
        ReactQuill: ReactQuill.default || ReactQuill,
        Quill: Quill,
        mounted: true
      });
    }
  }

  handleChange(content) {
    const { value, onChange } = this.props;
    // Only call onChange if the content actually changed
    if (content !== value && onChange) {
      onChange(content);
    }
  }

  imageHandler() {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');

    input.addEventListener('change', async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      // File type validation
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      // File size validation (10MB)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        alert('Image size must be less than 10MB');
        return;
      }

      const quill = this.quillRef.current.getEditor();
      const range = quill.getSelection(true);

      // Insert placeholder during upload
      quill.insertEmbed(range.index, 'image', 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7');
      quill.setSelection(range.index + 1);

      try {
        // Convert to base64
        const base64Image = await this.fileToBase64(file);

        // Get language and timeZone from props or default
        const { language = 'en', timeZone = 'UTC' } = this.props;

        // Upload
        const response = await saveImage(
          base64Image,
          '',  // url/postfix empty for inline images
          file.type,
          language,
          timeZone
        );

        if (response.success) {
          // Replace placeholder with CDN URL (HTTPS)
          const cdnUrl = `https://static.wannasport.dk/images/${response.data}`;
          quill.deleteText(range.index, 1);
          quill.insertEmbed(range.index, 'image', cdnUrl);
          quill.setSelection(range.index + 1);
        } else {
          quill.deleteText(range.index, 1);
          alert('Failed to upload image: ' + response.message);
        }
      } catch (error) {
        const currentRange = quill.getSelection();
        if (currentRange) {
          quill.deleteText(range.index, 1);
        }
        alert('Error: ' + error.message);
        console.error('Image upload error:', error);
      }
    });

    input.click();
  }

  fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }

  render() {
    const { value, placeholder = '', style = {} } = this.props;
    const { ReactQuill, Quill, mounted } = this.state;

    // Configure toolbar with advanced formatting options
    const colors = [
      // Grayscale
      "#000000", "#1a1a1a", "#333333", "#4d4d4d", "#666666", "#808080", "#999999", "#b3b3b3",
      "#cccccc", "#d9d9d9", "#e6e6e6", "#f2f2f2", "#ffffff",
      // Reds
      "#330000", "#660000", "#990000", "#cc0000", "#ff0000", "#ff3333", "#ff6666", "#ff9999",
      "#ffcccc", "#ffe6e6",
      // Oranges
      "#331a00", "#663300", "#994d00", "#cc6600", "#ff8000", "#ff9933", "#ffb366", "#ffcc99",
      "#ffe6cc", "#fff2e6",
      // Yellows
      "#333300", "#666600", "#999900", "#cccc00", "#ffff00", "#ffff33", "#ffff66", "#ffff99",
      "#ffffcc", "#ffffe6",
      // Greens
      "#003300", "#006600", "#009900", "#00cc00", "#00ff00", "#33ff33", "#66ff66", "#99ff99",
      "#ccffcc", "#e6ffe6",
      // Teals/Cyans
      "#003333", "#006666", "#009999", "#00cccc", "#00ffff", "#33ffff", "#66ffff", "#99ffff",
      "#ccffff", "#e6ffff",
      // Blues
      "#000033", "#000066", "#000099", "#0000cc", "#0000ff", "#3333ff", "#6666ff", "#9999ff",
      "#ccccff", "#e6e6ff",
      // Purples
      "#1a0033", "#330066", "#4d0099", "#6600cc", "#8000ff", "#9933ff", "#b366ff", "#cc99ff",
      "#e6ccff", "#f2e6ff",
      // Pinks
      "#330033", "#660066", "#990099", "#cc00cc", "#ff00ff", "#ff33ff", "#ff66ff", "#ff99ff",
      "#ffccff", "#ffe6ff",
      // Browns
      "#1a0d00", "#331a00", "#4d2600", "#663300", "#804000", "#995c00", "#b37700", "#cc9933",
      "#e6b366", "#f2d9b3"
    ];

    const modules = {
      toolbar: {
        container: [
          [
            { 'size': ['small', false, 'large', 'huge'] }
          ],
          [
            { 'bold': true },
            { 'italic': true },
            { 'underline': true },
            { 'strike': true }
          ],
          [
            { 'color': colors },
            { 'background': colors }
          ],
          [
            { 'blockquote': true },
            { 'code-block': true }
          ],
          [
            { 'list': 'ordered' },
            { 'list': 'bullet' }
          ],
          [
            { 'align': ['right', 'center', 'justify'] }
          ],
          [
            { 'link': true },
            { 'image': true }
          ]
        ],
        handlers: {
          image: this.imageHandler
        }
      },
      imageResize: Quill ? {
        parchment: Quill.import('parchment'),
        modules: ['Resize', 'DisplaySize', 'Toolbar']
      } : undefined
    };

    // Formats allowed in the editor
    const formats = [
      'font', 'size',
      'bold', 'italic', 'underline', 'strike',
      'color', 'background',
      'blockquote', 'code-block',
      'list', 'bullet',
      'align',
      'link',
      'image',
      'width',
      'height',
      'float',
      'style'
    ];

    // Show loading state while ReactQuill is being loaded
    if (!mounted || !ReactQuill) {
      return (
        <div style={{ ...style, width: '100%', padding: '10px', border: '1px solid #ccc', backgroundColor: '#f9f9f9' }}>
          Loading editor...
        </div>
      );
    }

    return (
      <div style={{ ...style, width: '100%' }}>
        <ReactQuill
          ref={this.quillRef}
          theme="snow"
          value={value || ''}
          onChange={this.handleChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          style={{
            backgroundColor: 'white',
            minHeight: '200px'
          }}
        />
        <style>{`
          .ql-container {
            min-height: 200px;
            font-size: 14px;
          }
          .ql-editor {
            min-height: 200px;
          }
        `}</style>
      </div>
    );
  }
}

export default RichTextEditor;
